import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, Typography, makeStyles } from '@material-ui/core'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import type { SpecIntelligenceErrorType } from 'api/graphQL/types/specIntelligence.types'
import React from 'react'

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: pxToRem(16),
        padding: pxToRem(24),
        textAlign: 'center',
    },
    icon: {
        fontSize: pxToRem(48),
        color: '#C62828',
    },
    title: {
        fontSize: pxToRem(15),
        fontWeight: 600,
        color: '#314959',
    },
    message: {
        fontSize: pxToRem(13),
        color: '#637381',
        maxWidth: pxToRem(280),
    },
}))

const ANALYSIS_ERROR_MESSAGE =
    'Unable to complete the analysis due to an unexpected error. Please try again. If issues persist, please contact support.'

const TOKEN_LIMIT_ERROR_MESSAGE = 'AI token limit exceeded. Please select fewer sections and try again.'

/** Props for {@link SpecIntelligenceErrorView}. */
export interface SpecIntelligenceErrorViewProps {
    /** Called when the user clicks Re-run Analysis. */
    onRerun: () => void
    /** Backend error classification for the failed report; drives which friendly message is shown. */
    errorType?: SpecIntelligenceErrorType | null
}

/**
 * Full-height error state shown when a SpecIntelligence run fails.
 * Displays a user-friendly message and a Re-run Analysis button.
 * Technical error details remain accessible via the network request for troubleshooting.
 */
const SpecIntelligenceErrorView = ({ onRerun, errorType }: SpecIntelligenceErrorViewProps) => {
    const classes = useStyles()
    const message = errorType === 'LlmPromptTooLarge' ? TOKEN_LIMIT_ERROR_MESSAGE : ANALYSIS_ERROR_MESSAGE
    return (
        <div className={classes.root} role="alert">
            <ErrorOutlineIcon className={classes.icon} aria-hidden="true" />
            <Typography component="h3" className={classes.title}>Analysis failed</Typography>
            <Typography className={classes.message}>{message}</Typography>
            <Button variant="contained" color="primary" size="small" onClick={onRerun}>
                Re-run Analysis
            </Button>
        </div>
    )
}

export default SpecIntelligenceErrorView
